import { Component, computed, inject, OnInit } from '@angular/core';
import { Client } from '../../services/client.interface';
import { ClientsService } from '../../services/clients.service';
import { Router } from '@angular/router';
import { SnackbarService } from '@/app/shared/services/snackbar.service';
import { AppConstants } from '@/app/constants/AppConstants';
import { TitlePagesComponent } from '@/app/shared/components/title-pages-component/title-pages-component';
import { TableComponent } from '@/app/shared/components/table-component/table-component';
import { ConfirmDeleteDialog } from '@/app/shared/components/confirm-delete-dialog/confirm-delete-dialog';
import { ClientStore } from '@/app/core/states/clientes-state/client-state.store';
import { OccupationsService } from '@/app/features/ocupaciones/services/occupations.service';
import { Occupation } from '@/app/features/ocupaciones/services/occupation.interface';
import { DepartamentService } from '@/app/features/departamentos/services/departament.service';
import { Departament } from '@/app/features/departamentos/services/departament.interface';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of, forkJoin, Subject } from 'rxjs';
import { CampanasService } from '@/app/features/campañas/services/campanas.service';
import { PaginatedResponse } from '@/app/features/campañas/services/campaign.interface';
import { ThemeService } from '@/app/core/services/theme/theme.service';

@Component({
  selector: 'app-clientes-component',
  standalone: true,
  imports: [TitlePagesComponent, TableComponent, ConfirmDeleteDialog],
  templateUrl: './clientes-component.html',
  styleUrl: './clientes-component.scss',
})
export class ClientesComponent implements OnInit {
  private themeService = inject(ThemeService);
  private clientsService = inject(ClientsService);
  private router = inject(Router);
  private snackbarService = inject(SnackbarService);
  private store = inject(ClientStore);
  private occupationService: OccupationsService = inject(OccupationsService);
  private departamentService: DepartamentService = inject(DepartamentService);
  private campanasService = inject(CampanasService);

  protected themeContent = this.themeService.themeContent;
  protected clientsComponent = computed(
    () => this.themeContent()?.clientsComponent
  );
  protected showBtn: boolean = true;
  protected clients: Client[] = [];
  protected activateMenu = true;

  // Pagination properties
  protected currentPage: number = 0; // 0-based for API
  protected pageSize: number = 10;
  protected totalElements: number = 0;
  protected searchTerm: string = '';
  protected loading: boolean = false;

  private readonly reload$ = new Subject<void>();
  protected iconActions: {
    label: string;
    icon: string;
    action: (item: any) => void;
  }[] = [
    {
      label: 'Iniciar flujo de campañas',
      icon: 'addNew.svg',
      action: (item) => {
        this.iniciarFlujoCampanas(item);
      },
    },
  ];

  //variables para el modal de confirmación de eliminación
  protected showModal: boolean = false;
  protected clientToDelete!: Client;
  protected message!: string;

  pageData = computed(() => ({
    title: this.clientsComponent()?.title || 'Titulo',
    subTitle: this.clientsComponent()?.description || 'Subtitulo',
  }));

  ngOnInit(): void {
    this.store.resetData();

    this.reload$
      .pipe(
        tap(() => (this.loading = true)),
        switchMap(() =>
          this.clientsService.getClientsPaginated(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          ),
        ),
      )
      .subscribe({
        next: (response) => {
          this.clients = response.content;
          this.totalElements = response.totalElements;
          this.loading = false;
        },
        error: () => (this.loading = false),
      });

    this.loadClients();
  }

  ngOnDestroy(): void {
    this.reload$.complete();
  }

  private loadClients(): void {
    this.reload$.next();
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.currentPage = 0;
    this.loadClients();
  }

  addClient = () => {
    this.router.navigate(['/clientes/crear']);
  };

  editClient = (client: Client) => {
    this.router.navigate(['/clientes/editar', client.publicId]);
  };

  viewResults = (client: Client) => {
    this.router.navigate(['/resultados'], {
      queryParams: { clienteId: client.publicId },
    });
  };

  //elimina el registro de cliente
  handleDelete() {
    this.clientsService.deleteClient(this.clientToDelete.publicId).subscribe({
      next: () => {
        this.loadClients(); // Refresh clients list after delete
        this.snackbarService.showSuccess('Cliente eliminado con éxito');
        this.showModal = false;
      },
      error: (err) => {
        this.snackbarService.showError(
          'Error al eliminar el cliente: ' + err.message
        );
      },
    });
  }

  //muestra el modal de confirmación de eliminación
  showConfirmDeleteDialog = (client: Client) => {
    //mensaje para la confirmación de eliminacion
    this.message = 'Se eliminará al cliente : ' + client.name;
    this.clientToDelete = client;
    this.showModal = true;
  };

  //cierra el modal de confirmación de eliminación
  handleCancelDeleteDialog() {
    this.showModal = false;
  }

  // Método para iniciar flujo de campañas - controla todo el flujo
  iniciarFlujoCampanas = (item: any) => {
    // Verificar si ya tenemos datos en cache del cliente correcto
    const currentClient = this.store.client();
    if (currentClient?.publicId === item.publicId && this.store.departaments().length > 0) {
      // Si hay departamentos en cache del cliente correcto, verificar si están completos
      const hasCompleteDepartments = this.store.departaments().every((dept: any) =>
        dept.occupations && dept.occupations.length > 0
      );

      if (hasCompleteDepartments) {
        // Si están completos, ir directamente a departamento sin modal
        this.router.navigate(['/clientes/departamento'], {
          queryParams: { showModal: 'false' },
        });
      } else {
        // Si no están completos, mostrar modal
        this.router.navigate(['/clientes/departamento'], {
          queryParams: { showModal: 'true' },
        });
      }
    } else {
      // Si no hay datos en cache o son de otro cliente, cargar desde API
      this.obtenerDatosCompletosCiente(item).subscribe({
        next: (result) => {
          // Mostrar modal solo si no tiene ningún departamento creado
          const showModal = !result.hasCompleteData;

          this.router.navigate(['/clientes/departamento'], {
            queryParams: { showModal: showModal.toString() },
          });
        },
        error: (error) => {
          console.error('Error al verificar datos del cliente:', error);
          this.snackbarService.showError(
            'Error al verificar los datos del cliente'
          );
          // En caso de error, navegar con modal por defecto
          this.router.navigate(['/clientes/departamento'], {
            queryParams: { showModal: 'true' },
          });
        },
      });
    }
  };

  /**
   * Método para cargar solo departamentos de un cliente
   * Retorna un Observable con los departamentos
   */
  private cargarDatosCliente(item: any): Observable<{
    departaments: Departament[];
  }> {
    this.store.setClient(item);

    // ✅ Obtenemos los departamentos
    return this.departamentService.getDepartaments(item.publicId).pipe(
      tap((departaments: Departament[]) => {
        this.store.setDepartaments(departaments);
      }),
      map((departaments: Departament[]) => ({
        departaments,
      }))
    );
  }

  /**
   * Método completo que incluye también información de campañas
   * Para usar en el flujo de iniciar campañas
   */
  private obtenerDatosCompletosCiente(item: any): Observable<{
    departaments: Departament[];
    campaigns: any[];
    hasCompleteData: boolean;
  }> {
    return this.cargarDatosCliente(item).pipe(
      switchMap((basicData) => {
        return this.campanasService.getCampaigns(item.publicId).pipe(
          map((response: PaginatedResponse<any>) => {
            const campaigns = response.content;
            this.store.setCampaigns(campaigns); // Guardar en cache
            return {
              ...basicData,
              campaigns: campaigns,
              hasCompleteData: basicData.departaments.length > 0,
            };
          })
        );
      })
    );
  }

  mostrarOcupaciones = (item: any) => {
    this.cargarDatosCliente(item)
      .pipe(
        finalize(() => {
          if (this.store.departaments().length > 0) {
            this.router.navigate(['/clientes/ocupacion']);
          } else {
            this.snackbarService.showInfo('No hay datos para ocupaciones');
          }
        })
      )
      .subscribe({
        error: (error) => {
          console.error('Error al cargar datos:', error);
          this.snackbarService.showError(
            'Error al cargar los datos del cliente'
          );
        },
      });
  };

  mostrarDeparmatentos = (item: any) => {
    // Verificar si ya tenemos departamentos en cache del cliente correcto
    const currentClient = this.store.client();
    if (currentClient?.publicId === item.publicId && this.store.departaments().length > 0) {
      this.router.navigate(['/clientes/departamento']);
    } else {
      // Si no hay en cache o son de otro cliente, cargar desde API
      this.cargarDatosCliente(item)
        .pipe(
          finalize(() => {
            if (this.store.departaments().length > 0) {
              this.router.navigate(['/clientes/departamento']);
            } else {
              this.snackbarService.showInfo('No hay datos para departamentos. Intenta crear uno nuevo con el botón +');
            }
          })
        )
        .subscribe({
          error: (error) => {
            console.error('Error al cargar datos:', error);
            this.snackbarService.showError(
              'Error al cargar los datos del cliente'
            );
          },
        });
    }
  };

  mostrarCampanas = (item: any) => {
    // Verificar si ya tenemos campañas en cache del cliente correcto
    const currentClient = this.store.client();
    if (currentClient?.publicId === item.publicId && this.store.campaigns().length > 0) {
      this.router.navigate(['/clientes/campañas'], {
        queryParams: { viewOnly: 'true' }
      });
    } else {
      // Si no hay en cache o son de otro cliente, cargar campañas
      this.obtenerDatosCompletosCiente(item).subscribe({
        next: (result) => {
          if (this.store.campaigns().length > 0) {
            this.router.navigate(['/clientes/campañas'], {
              queryParams: { viewOnly: 'true' }
            });
          } else {
            this.snackbarService.showInfo('No hay datos para campañas. Intenta iniciar una nueva con el botón +');
          }
        },
        error: (error) => {
          console.error('Error al cargar datos:', error);
          this.snackbarService.showError(
            'Error al cargar los datos del cliente'
          );
        },
      });
    }
  };

  opciones = [
    // { label: 'Iniciar flujo de campañas', action: this.iniciarFlujoCampanas },
    { label: 'Ver Campañas', action: this.mostrarCampanas },
    { label: 'Ver Departamentos', action: this.mostrarDeparmatentos },
    { label: 'Ver Resultados', action: this.viewResults },
  ];

  onItemsPerPageChange(newSize: number): void {
    this.pageSize = newSize;
    this.currentPage = 0;
    this.loadClients();
  }

  onPageChange(newPage: number): void {
    this.currentPage = newPage - 1; // Convert to 0-based
    this.loadClients();
  }

  columnDefinitions = [
    { key: 'name', displayName: 'Nombre del cliente' },
    { key: 'rfc', displayName: 'RFC' },
    { key: 'contactName', displayName: 'Nombre de contacto' },
    { key: 'contactPhone', displayName: 'Teléfono de contacto' },
    { key: 'contactEmail', displayName: 'Email de contacto' },
    { key: 'acciones', displayName: 'Acciones' },
  ];
}
